<template>
    <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-800">Campus Comparison Across Years</h3>
            <div class="text-sm text-gray-500">Expected Graduates by Year & Campus</div>
        </div>
        <apexchart type="bar" :options="chartOptions" :series="chartSeries" height="400" />
    </div>
</template>

<script>
export default {
    name: "CampusComparisonChart",
    props: {
        data: {
            type: [Array, Object],
            required: true,
            default: () => []
        }
    },
    computed: {
        // Check if data is in new format (Array) or old format (Object)
        isNewFormat() {
            return Array.isArray(this.data);
        },
        chartSeries() {
            if (this.isNewFormat) {
                // New format: Array of years with campuses
                const allCampusCodes = new Set();
                this.data.forEach(year => {
                    year.campuses.forEach(campus => {
                        allCampusCodes.add(campus.campus_code);
                    });
                });

                // Create series for each campus
                return Array.from(allCampusCodes).map(campusCode => {
                    const campusData = this.data.map(year => {
                        const campus = year.campuses.find(c => c.campus_code === campusCode);
                        return campus ? campus.expected_graduate_count : 0;
                    });

                    return {
                        name: campusCode,
                        data: campusData
                    };
                });
            } else {
                // Old format: Object with years, campuses, and data
                return this.data.campuses.map(campus => ({
                    name: campus,
                    data: this.data.data[campus] || []
                }));
            }
        },
        yearLabels() {
            if (this.isNewFormat) {
                return this.data.map(year => year.student_level);
            } else {
                return this.data.years || [];
            }
        },
        chartOptions() {
            return {
                chart: {
                    type: 'bar',
                    stacked: false,
                    toolbar: {
                        show: false
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '55%',
                        dataLabels: {
                            position: 'top',
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val > 0 ? val.toString() : '';
                    },
                    style: {
                        fontSize: '11px',
                        fontWeight: 'bold',
                        colors: ['#ffffff']
                    }
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                xaxis: {
                    categories: this.yearLabels,
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '600',
                            colors: '#374151'
                        }
                    },
                    title: {
                        text: 'Academic Years',
                        style: {
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151'
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Students',
                        style: {
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151'
                        }
                    },
                    labels: {
                        formatter: function (val) {
                            return val.toLocaleString();
                        }
                    }
                },
                colors: ['#0f766e', '#f59e0b', '#92400e', '#dc2626', '#7c3aed', '#059669'], // Teal, Gold, Brown, Red, Purple, Green
                fill: {
                    type: 'solid',
                    opacity: 1
                },
                grid: {
                    borderColor: '#e5e7eb',
                    strokeDashArray: 4,
                    xaxis: {
                        lines: {
                            show: true
                        }
                    },
                    yaxis: {
                        lines: {
                            show: true
                        }
                    }
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val.toLocaleString() + ' graduates';
                        }
                    },
                    theme: 'light'
                },
                legend: {
                    position: 'top',
                    horizontalAlign: 'center',
                    labels: {
                        colors: '#374151'
                    }
                },
                responsive: [
                    {
                        breakpoint: 768,
                        options: {
                            plotOptions: {
                                bar: {
                                    columnWidth: '70%'
                                }
                            },
                            dataLabels: {
                                fontSize: '10px'
                            },
                            legend: {
                                position: 'bottom'
                            }
                        }
                    }
                ]
            };
        }
    }
};
</script>

<style scoped>
/* Component-specific styles can be added here */
</style>